/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * UI v2 apps on the client: pure helpers over the `apps` payload of
 * GET /module-entitlements (backend/app/Services/app_registry.py).
 *
 * An app narrows and relabels the sidebar the user already has; it never adds
 * an item. Route keys no app owns (dashboards, reports, custom objects) show
 * in every app. With no apps (an older backend, or still loading) everything
 * behaves exactly as before.
 */

import { createContext, useContext } from "react";

/** The app in force: the user's last choice if still available, else the tenant default, else the first. */
export function resolveActiveApp(apps, storedCode, defaultCode) {
  if (!Array.isArray(apps) || apps.length === 0) return null;
  return (
    apps.find((a) => a.code === storedCode) ||
    apps.find((a) => a.code === defaultCode) ||
    apps[0]
  );
}

function owns(app, key) {
  return Boolean(app && key && Array.isArray(app.route_keys) && app.route_keys.includes(key));
}

/**
 * Which app should be active when the user lands on `routeKey`: stay put when
 * the current app owns it (shared objects like contacts live in several apps)
 * or nobody does; otherwise switch to the first app that owns it, so a record
 * opened from search or a link lands in its own app.
 */
export function appForRouteKey(apps, ownedKeys, routeKey, current) {
  if (!routeKey || !Array.isArray(apps) || apps.length === 0) return current || null;
  if (owns(current, routeKey)) return current;
  if (!ownedKeys.has(routeKey)) return current || null;
  return apps.find((a) => owns(a, routeKey)) || current || null;
}

export function visibleInApp(key, app, ownedKeys) {
  if (!app) return true;
  if (!ownedKeys.has(key)) return true;
  return owns(app, key);
}

/** Filter and relabel sidebar sections (from sidebarModel.buildSidebarSections) for an app. */
export function applyApp(sections, app, ownedKeys) {
  if (!app) return sections;
  const label = (item) => ({ ...item, name: app.labels?.[item.key] || item.name });
  return sections
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (!item.children.length) return visibleInApp(item.key, app, ownedKeys) ? label(item) : null;
          const children = item.children.filter((c) => visibleInApp(c.key, app, ownedKeys)).map(label);
          // Keep a group whose children survive; a group with its own route
          // follows the same rule as any other link.
          if (children.length) return { ...label(item), children };
          return item.route && visibleInApp(item.key, app, ownedKeys) ? { ...label(item), children: [] } : null;
        })
        .filter(Boolean),
    }))
    .filter((section) => section.items.length > 0);
}

/** Shared by AppFrame (provider), the sidebar, header and breadcrumbs. */
export const ActiveAppContext = createContext({ app: null, apps: [], ownedKeys: new Set(), setApp: () => {} });

export function useActiveApp() {
  return useContext(ActiveAppContext);
}
